import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import Button from '@/shared/components/Button/Button'
import Modal from '@/shared/components/Modal/Modal'
import ConfirmationModal from '@/shared/components/Modal/ConfirmationModal/ConfirmationModal'
import { useRestaurantSettingsHydration } from '@/features/restaurant-settings'
import { SettingsSection, useSettingsModal } from './store/useSettingsModal'
import GeneralSection from './sections/GeneralSection'
import AccountSection from './sections/AccountSection'
import RestaurantSection from './sections/RestaurantSection'
import DeliveryConnections from '@/features/delivery-connections/DeliveryConnections'
import styles from './SettingsModal.module.scss'

interface NavEntry {
  section: SettingsSection
  icon: string
  labelKey: string
  /** Says what the section holds, so it does not repeat the entry's label. */
  headingKey: string
}

const NAV: NavEntry[] = [
  {
    section: 'integrations',
    icon: 'las la-plug',
    labelKey: 'DELIVERY.NAV',
    headingKey: 'DELIVERY.HEADING'
  },
  {
    section: 'general',
    icon: 'las la-sliders-h',
    labelKey: 'SETTINGS.NAV.GENERAL',
    headingKey: 'SETTINGS.HEADING.GENERAL'
  },
  {
    section: 'account',
    icon: 'las la-user',
    labelKey: 'SETTINGS.NAV.ACCOUNT',
    headingKey: 'SETTINGS.HEADING.ACCOUNT'
  },
  {
    section: 'restaurant',
    icon: 'las la-store',
    labelKey: 'SETTINGS.NAV.RESTAURANT',
    headingKey: 'SETTINGS.HEADING.RESTAURANT'
  }
]

type Destination = SettingsSection | 'close'

/**
 * Settings in one dialog, laid out as Claude's are: sections down the left,
 * the open one on the right.
 *
 * Each section with a Save reports the edits it has not saved yet. Leaving
 * it, for another section or by closing, unmounts it and so throws those
 * away, which is why both go through one confirmation. The global form-dirty
 * flag is left alone: it belongs to the page underneath and its route guard.
 */
function SettingsModal() {
  const { t } = useTranslation()
  const section = useSettingsModal(s => s.section)
  const open = useSettingsModal(s => s.open)
  const close = useSettingsModal(s => s.close)

  const [hasUnsaved, setHasUnsaved] = useState(false)
  const [pending, setPending] = useState<Destination | null>(null)

  // The modal is mounted on every signed-in page, so the venue's facts are
  // brought up to date once, early, for the website builder that reads them too.
  useRestaurantSettingsHydration()

  const go = (destination: Destination) => {
    setHasUnsaved(false)
    if (destination === 'close') close()
    else open(destination)
  }

  const request = (destination: Destination) => {
    if (destination === section) return
    if (hasUnsaved) setPending(destination)
    else go(destination)
  }

  const current = NAV.find(entry => entry.section === section)

  return (
    <>
      <Modal
        title={
          <>
            <span className={styles.titleNav}>{t('SETTINGS.TITLE')}</span>
            {current && t(current.headingKey)}
          </>
        }
        size="xlarge"
        disablePortal={false}
        isOpen={section !== null}
        onClose={() => request('close')}
      >
        <div className={styles.settings}>
          <span className={styles.divider} aria-hidden />
          <nav className={styles.nav} aria-label={t('SETTINGS.TITLE')}>
            {NAV.map(entry => (
              <Button
                key={entry.section}
                label={t(entry.labelKey)}
                variant="text"
                color={section === entry.section ? 'secondary' : 'primary'}
                textAlignment="left"
                icon={entry.icon}
                condensedLabel
                onClick={() => request(entry.section)}
              />
            ))}
          </nav>

          {section === 'general' && <GeneralSection />}
          {section === 'integrations' && <DeliveryConnections />}
          {section === 'account' && (
            <AccountSection onUnsavedChange={setHasUnsaved} />
          )}
          {section === 'restaurant' && (
            <RestaurantSection onUnsavedChange={setHasUnsaved} />
          )}
        </div>
      </Modal>

      <ConfirmationModal
        isOpen={pending !== null}
        title={t('SETTINGS.UNSAVED_TITLE')}
        message={t('SETTINGS.UNSAVED_MESSAGE')}
        onClose={() => setPending(null)}
        resetsFormDirty={false}
        onConfirm={() => {
          if (pending) go(pending)
          setPending(null)
        }}
      />
    </>
  )
}

export default SettingsModal
